// FILE: components/cards/PathCard.tsx
// Unified PathCard — dùng chung cho /path (list) và /explore (grid)
// 2 trạng thái: isUnlocked=false (sale mode) / isUnlocked=true (learning mode)
// 2 variant: "grid" (vertical thumbnail) / "list" (horizontal compact)
"use client";

import { hardNavigate } from "@/components/shared/HardNavLink";
import HardNavLink from "@/components/shared/HardNavLink";
import UserHoverCard from "@/components/shared/UserHoverCard";
import { Star, Users, Play, Gem, Unlock, BookOpen, ArrowRight, CheckCircle2 } from "lucide-react";
/* ── Types ── */

export interface PathCardProps {
  id: string;
  title: string;
  slug?: string;
  author: string;
  authorId?: string;
  authorUsername?: string | null;
  emoji?: string;
  org?: string;
  orgHref?: string;
  position?: string;
  positionHref?: string;
  rating?: number;
  students?: number;
  questions?: number;
  demoQuestions?: number;
  price?: number | "free";
  gradient?: string;
  // Status:
  isUnlocked?: boolean;
  progress?: number; // 0–100
  isOwner?: boolean;
  // Layout:
  variant?: "grid" | "list";
  onClick?: () => void;
}

/* ── Helpers ── */

function formatPrice(price: number | "free"): string {
  if (price === "free" || price === 0) return "Miễn phí";
  return `${(Number(price) * 1000).toLocaleString("vi")}đ`;
}

function progressLabel(pct: number): string {
  if (pct === 0) return "Bắt đầu học";
  if (pct >= 100) return "Hoàn thành!";
  return "Học tiếp";
}

/** Org + Position badges (giống ExamCard) */
function OrgPositionBadges({
  org,
  orgHref,
  position,
  positionHref,
}: {
  org?: string;
  orgHref?: string;
  position?: string;
  positionHref?: string;
}) {
  if (!org && !position) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-2">
      {org && (orgHref ? (
        <span role="link" tabIndex={0}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); hardNavigate(orgHref); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); hardNavigate(orgHref); } }}
          className="cursor-pointer text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 transition-colors">🏦 {org}</span>
      ) : (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">🏦 {org}</span>
      ))}
      {position && (positionHref ? (
        <span role="link" tabIndex={0}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); hardNavigate(positionHref); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); hardNavigate(positionHref); } }}
          className="cursor-pointer text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200 hover:bg-teal-100 hover:border-teal-400 transition-colors">💼 {position}</span>
      ) : (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200">💼 {position}</span>
      ))}
    </div>
  );
}

/* ── Sub-components ── */

/** Visual header — gradient block + emoji + badge */
function CardVisual({
  emoji,
  gradient,
  price,
  isUnlocked,
  progress,
  variant,
}: {
  emoji: string;
  gradient: string;
  price: number | "free";
  isUnlocked: boolean;
  progress: number;
  variant: "grid" | "list";
}) {
  const isFree = price === "free" || price === 0;

  if (variant === "list") {
    return (
      <div className={`w-16 sm:w-20 shrink-0 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center relative overflow-hidden self-stretch`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,.12),transparent_60%)]" />
        <span className="text-2xl sm:text-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,.2)] group-hover:scale-110 transition-transform duration-300">
          {emoji}
        </span>
        {/* Unlocked → progress ring, Locked → price badge */}
        {isUnlocked && progress > 0 && (
          <div className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
            <span className="text-[8px] font-extrabold text-teal-700">{progress}%</span>
          </div>
        )}
      </div>
    );
  }

  // variant === "grid"
  return (
    <div className={`h-28 sm:h-32 bg-gradient-to-br ${gradient} relative flex items-center justify-center overflow-hidden`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,.1),transparent_60%)]" />
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fff' fill-opacity='1'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E\")",
      }} />
      <span className="text-5xl drop-shadow-[0_4px_12px_rgba(0,0,0,.25)] group-hover:scale-110 transition-transform duration-300">
        {emoji}
      </span>
      {/* Top-right badge */}
      <div className="absolute top-2.5 right-2.5">
        {isUnlocked ? (
          progress >= 100 ? (
            <span className="px-2 py-1 rounded-full text-[9px] font-bold bg-emerald-500 text-white shadow-sm flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Hoàn thành
            </span>
          ) : null
        ) : isFree ? (
          <span className="px-2 py-1 rounded-full text-[9px] font-bold bg-emerald-500 text-white shadow-sm flex items-center gap-1">
            <Unlock className="w-3 h-3" /> Free
          </span>
        ) : (
          <span className="px-2 py-1 rounded-full text-[9px] font-bold bg-amber-500 text-white shadow-sm flex items-center gap-1">
            <Gem className="w-3 h-3" /> {price}
          </span>
        )}
      </div>
    </div>
  );
}

/** Meta stats — rating, students, questions */
function CardMeta({
  rating = 0,
  students = 0,
  questions = 0,
  variant,
}: {
  rating?: number;
  students?: number;
  questions?: number;
  variant: "grid" | "list";
}) {
  const cls = variant === "list" ? "text-[10px]" : "text-[11px]";
  return (
    <div className={`flex items-center gap-2.5 ${cls} text-[#64748b] flex-wrap`}>
      {rating > 0 && (
        <span className="flex items-center gap-0.5 text-amber-500 font-semibold">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          {rating.toFixed(1)}
        </span>
      )}
      {students > 0 && (
        <span className="flex items-center gap-0.5">
          <Users className="w-3 h-3" />
          {students >= 1000 ? `${(students / 1000).toFixed(1)}k` : students}
        </span>
      )}
      <span className="flex items-center gap-0.5">
        <BookOpen className="w-3 h-3" />
        {questions} câu
      </span>
    </div>
  );
}

/** Progress bar — chỉ hiện khi unlocked */
function ProgressBar({ progress, gradient }: { progress: number; gradient: string }) {
  return (
    <div className="mt-2.5 mb-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-[#64748b]">Tiến độ</span>
        <span className="text-[11px] font-extrabold text-teal-700">{progress}%</span>
      </div>
      <div className="h-1.5 bg-[#e2e8f0] rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${gradient} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
    </div>
  );
}

/** Footer CTA — sale mode vs learning mode */
function CardFooter({
  isUnlocked,
  progress,
  price,
  demoQuestions,
  variant,
  isOwner,
}: {
  isUnlocked: boolean;
  progress: number;
  price: number | "free";
  demoQuestions: number;
  variant: "grid" | "list";
  isOwner: boolean;
}) {
  const isFree = price === "free" || price === 0;

  // Owner → Management mode
  if (isOwner) {
    return (
      <div className={`flex items-center justify-between ${variant === "list" ? "mt-2" : "pt-3 border-t border-[#f1f5f9] mt-auto"}`}>
        <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
          🔖 Của bạn
        </span>
        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#e2e8f0] text-[#64748b] text-[11px] font-bold hover:border-teal-400 hover:text-teal-700 transition-colors">
          Quản lý
        </span>
      </div>
    );
  }

  if (isUnlocked) {
    // ── Learning mode ──
    return (
      <div className={`${variant === "list" ? "mt-2" : "pt-3 border-t border-[#f1f5f9] mt-auto"}`}>
        {/* Đã mua badge — chỉ hiện khi không miễn phí (để user nhớ đã mua bao tiền) */}
        {!isFree && (
          <div className="mb-1.5">
            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-400">
              <CheckCircle2 className="w-2.5 h-2.5" />
              Đã mua · {price} 💎
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          {progress >= 100 ? (
            <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Đã hoàn thành
            </span>
          ) : (
            <span className="text-[11px] font-semibold text-[#64748b]">
              {progress > 0 ? `Còn ${100 - progress}% nữa` : "Chưa bắt đầu"}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-teal-700 text-white text-[11px] font-bold hover:bg-teal-600 transition-colors">
            <Play className="w-3 h-3" />
            {progressLabel(progress)}
          </span>
        </div>
      </div>
    );
  }

  // ── Sale mode ──
  return (
    <div className={`${variant === "list" ? "mt-2" : "pt-3 border-t border-[#f1f5f9] mt-auto"}`}>
      {/* Demo badge */}
      {demoQuestions > 0 && (
        <div className="mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <Unlock className="w-2.5 h-2.5" />
            {demoQuestions} câu demo miễn phí
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-extrabold">
          {isFree ? (
            <span className="text-emerald-600">Miễn phí</span>
          ) : (
            <span className="text-orange-600">{formatPrice(price)}</span>
          )}
        </span>
        {isFree ? (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-teal-700 text-white text-[11px] font-bold hover:bg-teal-600 transition-colors">
            <Play className="w-3 h-3" /> Học ngay
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold hover:bg-orange-600 transition-colors shadow-[0_2px_8px_rgba(249,115,22,.25)]">
            <Gem className="w-3 h-3" /> Mở khóa
          </span>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════ */

export default function PathCard({
  id,
  title,
  slug,
  author,
  authorId,
  authorUsername,
  emoji = "📚",
  org,
  orgHref,
  position,
  positionHref,
  rating = 0,
  students = 0,
  questions = 0,
  demoQuestions = 0,
  price = "free",
  gradient = "from-[#0f3a4a] to-[#1a5570]",
  isUnlocked = false,
  progress = 0,
  isOwner = false,
  variant = "grid",
  onClick,
}: PathCardProps) {
  const href = `/paths/${slug || id}`;

  const content = variant === "grid" ? (
    // ═══ GRID VARIANT (vertical thumbnail) ═══
    <>
      <CardVisual emoji={emoji} gradient={gradient} price={price} isUnlocked={isUnlocked} progress={progress} variant="grid" />

      <div className="p-4 flex flex-col flex-1">
        <OrgPositionBadges org={org} orgHref={orgHref} position={position} positionHref={positionHref} />

        {/* Title */}
        <h3 className="text-[14px] font-bold text-[#0f172a] leading-snug line-clamp-2 mb-1 flex-grow group-hover:text-teal-700 transition-colors">
          {title}
        </h3>

        {/* Author */}
        <p className="text-[11px] text-[#64748b] mb-2.5">
          by{" "}
          {authorId ? (
            <UserHoverCard
              userId={authorUsername || authorId}
              name={author}
              className="text-[11px] text-teal-700"
            />
          ) : (
            <span className="font-semibold text-teal-700">{author}</span>
          )}
        </p>

        {/* Meta */}
        <CardMeta rating={rating} students={students} questions={questions} variant="grid" />

        {/* Progress (only unlocked) */}
        {isUnlocked && <ProgressBar progress={progress} gradient={gradient} />}

        {/* Footer CTA */}
        <CardFooter isUnlocked={isUnlocked} progress={progress} price={price} demoQuestions={demoQuestions} variant="grid" isOwner={isOwner} />
      </div>
    </>
  ) : (
    // ═══ LIST VARIANT (horizontal compact) ═══
    <div className="flex gap-3 p-3 sm:p-4">
      <CardVisual emoji={emoji} gradient={gradient} price={price} isUnlocked={isUnlocked} progress={progress} variant="list" />

      <div className="flex-1 min-w-0 flex flex-col">
        <OrgPositionBadges org={org} orgHref={orgHref} position={position} positionHref={positionHref} />

        {/* Title */}
        <h3 className="text-[13px] sm:text-[14px] font-bold text-[#0f172a] leading-snug line-clamp-2 mb-0.5 group-hover:text-teal-700 transition-colors">
          {title}
        </h3>

        {/* Author */}
        <p className="text-[11px] text-[#64748b] mb-2">
          {authorId ? (
            <UserHoverCard
              userId={authorUsername || authorId}
              name={author}
              className="text-[11px] text-teal-700"
            />
          ) : (
            author
          )}
        </p>

        {/* Meta */}
        <CardMeta rating={rating} students={students} questions={questions} variant="list" />

        {/* Progress (only unlocked) */}
        {isUnlocked && <ProgressBar progress={progress} gradient={gradient} />}

        {/* Footer CTA */}
        <CardFooter isUnlocked={isUnlocked} progress={progress} price={price} demoQuestions={demoQuestions} variant="list" isOwner={isOwner} />
      </div>
    </div>
  );

  // Wrap in Link or div
  if (onClick) {
    return (
      <div
        onClick={onClick}
        className="bg-white border border-[#e2e8f0] rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,.08)] hover:-translate-y-0.5 hover:border-teal-400/40 active:scale-[0.98] active:translate-y-0 transition-all duration-200 cursor-pointer group flex flex-col"
      >
        {content}
      </div>
    );
  }

  return (
    <HardNavLink
      href={href}
      className="bg-white border border-[#e2e8f0] rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,.08)] hover:-translate-y-0.5 hover:border-teal-400/40 active:scale-[0.98] active:translate-y-0 transition-all duration-200 cursor-pointer group flex flex-col"
    >
      {content}
    </HardNavLink>
  );
}
